import { useMemo } from "react";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { useGetUser } from "../api/queries/useGetUser";
import {
  resolveMembershipIdentity,
  type MembershipIdentity,
} from "../utils/membershipIdentity";
import type { User } from "../types/Types";

export type CurrentMembership = {
  /** Unpaid Explorer (limits, locked nav, photo prompt, etc.). */
  isExplorer: boolean;
  /** Active paid SMBF member (Business / Executive / Early Stage / Youth). */
  isMember: boolean;
  /** Full identity (tier, labels, badge keys). */
  identity: MembershipIdentity;
  /** Best-available user row for the signed-in account. */
  profile: User | null | undefined;
  isLoading: boolean;
  /** Logged in and profile identity resolved. */
  isReady: boolean;
};

/**
 * Single hook for the signed-in user: Explorer vs paid member.
 *
 * Prefer this over calling `isUnpaidExplorerUser` / membershipName checks in components.
 * For *other* users (lists, attendees), use `resolveMembershipIdentity(user)` instead.
 */
export function useCurrentMembership(): CurrentMembership {
  const { objectId, user } = useAppContext();
  const authed = isLoggedIn();
  const id = objectId || "";

  const { data: userData, isLoading } = useGetUser({
    id,
    enabled: authed && !!id,
  });

  // Photo URLs live on the sibling `imageUrls` bag from GetUser — merge like
  // Dashboard / MyAccount so hasProfessionalPhoto and badges see them.
  const profile = useMemo(() => {
    const response = userData ?? user;
    const row = response?.user;
    if (!row) return row;
    const images = response?.imageUrls;
    if (!images) return row;
    return { ...row, ...images, imageUrls: images } as User;
  }, [userData, user]);

  const identity = useMemo(
    () => resolveMembershipIdentity(profile),
    [profile],
  );

  return {
    isExplorer: identity.isExplorer,
    isMember: identity.isPaidMember,
    identity,
    profile,
    isLoading: authed && !!id && isLoading && !profile,
    isReady: !!profile,
  };
}
